<template>
  <el-drawer
    :visible="true"
    title="票匣詳情"
    size="480px"
    :before-close="onClose"
  >
    <div v-loading="loading" class="drawer-content px-[20px]">
      <!-- 票匣區塊 -->
      <section class="mb-[24px]">
        <div class="section-title mb-[12px]">票匣</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">會員名稱</span>
            <span
              class="text-primary-100 underline cursor-pointer"
              @click="goMemberProfile"
            >
              {{ memberName }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">會員手機</span>
            <span>{{ memberPhone }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">票匣名稱</span>
            <span
              class="text-primary-100 underline cursor-pointer"
              @click="goTicketBoxSetting"
            >
              {{ record.name }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">票匣編號</span>
            <span>{{ record.code || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">票匣來源</span>
            <span>{{ originLabel }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">發送日期</span>
            <span>{{ formatDateTime(record.createdAt) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">到期日期</span>
            <span>{{ record.exp ? formatDateTime(record.exp) : '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">原始核銷張數</span>
            <span>{{ record.availableTimes }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">使用狀態</span>
            <Tag :type="statusTagType">{{ statusLabelText }}</Tag>
          </div>
        </div>
        <div v-if="showVoidBtn" class="flex justify-end mt-[12px]">
          <BaseElButton plain @click="$emit('void')">
            作廢票匣
          </BaseElButton>
        </div>
      </section>

      <!-- 紀錄區塊 -->
      <el-divider />
      <section>
        <div class="section-title mb-[12px]">紀錄</div>
        <div v-if="recordUses.length === 0" class="text-gray-60 text-sub">
          暫無紀錄
        </div>

        <!-- 獲得紀錄 -->
        <template v-if="gainRecords.length > 0">
          <div class="subsection-title mb-[8px]">獲得紀錄</div>
          <div
            v-for="(use, idx) in gainRecords"
            :key="use.id || idx"
            class="record-item flex flex-col gap-[8px] mb-[12px] bg-gray-10 rounded-[4px]"
          >
            <div class="info-item">
              <span class="info-label">紀錄來源</span>
              <span>{{ useTypeLabel(use) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">紀錄建立時間</span>
              <span>{{ formatDateTime(use.createdAt) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">發送數量</span>
              <span>{{ use.remainTimes }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">剩餘核銷張數</span>
              <span>{{ use.remainTimes }}</span>
            </div>
          </div>
        </template>

        <!-- 使用紀錄 -->
        <template v-if="useRecords.length > 0">
          <div :class="{ 'mt-[16px]': gainRecords.length > 0 }" class="subsection-title mb-[8px]">使用紀錄</div>
          <div
            v-for="(use, idx) in useRecords"
            :key="use.id || idx"
            class="record-item flex flex-col gap-[8px] mb-[12px] bg-gray-10 rounded-[4px]"
          >
            <div class="info-item">
              <span class="info-label">紀錄來源</span>
              <span>{{ useTypeLabel(use) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">紀錄建立時間</span>
              <span>{{ formatDateTime(use.createdAt) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">{{ useTimesLabel(use) }}</span>
              <span>{{ use.times }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">剩餘核銷張數</span>
              <span>{{ use.remainTimes }}</span>
            </div>
          </div>
        </template>
      </section>
    </div>
  </el-drawer>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router/composables'
import { useShop } from '@/use/shop'
import { FindTicketBoxRecord } from '@/api/ticketBoxRecord'
import { formatDate } from '@/utils/date'
import { get } from 'lodash'
import Tag from '@/components/Tag/Tag.vue'

export default defineComponent({
  name: 'TicketBoxDetailDrawer',
  components: { Tag },
  props: {
    recordId: { type: String, required: true },
  },
  emits: ['close', 'void', 'refresh'],
  setup (props, { emit }) {
    const router = useRouter()
    const { shopId } = useShop()
    const loading = ref(false)
    const record = ref({})
    const recordUses = ref([])

    const createTypes = ['CREATE', 'SYSTEM_CREATE']
    const gainRecords = computed(() => recordUses.value.filter(u => createTypes.includes(u.type)))
    const useRecords = computed(() => recordUses.value.filter(u => !createTypes.includes(u.type)))

    const memberName = computed(() => get(record.value, 'Member.UserInfo.name', '-'))
    const memberPhone = computed(() => get(record.value, 'Member.UserInfo.phone', '-'))

    const originLabel = computed(() => {
      const map = { eventPlaybook: '系統行銷活動發放', admin: '後台新增', system: '系統新增' }
      return map[record.value.origin] || '-'
    })

    const statusLabelText = computed(() => {
      const map = { open: '未使用', available: '可使用', complete: '使用完畢', overdue: '已過期', voided: '已作廢' }
      return map[record.value.status] || '-'
    })

    const statusTagType = computed(() => {
      const map = { open: 'info', available: 'action', complete: 'success', overdue: 'warning', voided: 'danger' }
      return map[record.value.status] || 'info'
    })

    const showVoidBtn = computed(() => {
      return ['open', 'available'].includes(record.value.status)
    })

    const formatDateTime = (time) => time ? formatDate(time, 'YYYY/MM/DD HH:mm') : '-'

    const useTypeLabel = (use) => {
      if (createTypes.includes(use.type)) {
        const originMap = { eventPlaybook: '系統行銷活動發放', admin: '後台新增', system: '系統新增' }
        return originMap[use.origin] || '-'
      }
      const map = {
        ADMIN_USE: '後台核銷',
        SYSTEM_USE: '系統核銷',
        VOID_CANCEL: '後台作廢',
        SYSTEM_VOID: '系統作廢',
        SYSTEM_OVERDUE: '票匣過期',
        CLIENT_USE: '會員使用核銷',
      }
      return map[use.type] || '-'
    }

    const useTimesLabel = (use) => {
      if (['VOID_CANCEL', 'SYSTEM_VOID'].includes(use.type)) return '當次作廢數量'
      if (use.type === 'SYSTEM_OVERDUE') return '當次過期數量'
      if (['ADMIN_USE', 'SYSTEM_USE', 'CLIENT_USE'].includes(use.type)) return '當次核銷數量'
      return '數量'
    }

    const fetchDetail = async () => {
      loading.value = true
      const [res, err] = await FindTicketBoxRecord({
        shopId: shopId.value,
        id: props.recordId,
      })
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }
      record.value = res
      recordUses.value = res.TicketBoxRecordUses || []
    }

    const goMemberProfile = () => {
      const memberId = get(record.value, 'MemberId')
      if (!memberId) return
      router.push({
        name: 'MemberProfile',
        params: { id: memberId },
        query: { tab: 'ticketBoxHistory' },
      })
    }

    const goTicketBoxSetting = () => {
      router.push({
        name: 'TicketBoxSetting',
        query: { name: record.value.name },
      })
    }

    const onClose = () => emit('close')

    onMounted(() => {
      fetchDetail()
    })

    return {
      loading,
      record,
      recordUses,
      gainRecords,
      useRecords,
      memberName,
      memberPhone,
      originLabel,
      statusLabelText,
      statusTagType,
      showVoidBtn,
      formatDateTime,
      useTypeLabel,
      useTimesLabel,
      goMemberProfile,
      goTicketBoxSetting,
      onClose,
    }
  },
})
</script>

<style lang="postcss" scoped>
.section-title {
  @apply text-normal font-medium text-primary-100;
}

.subsection-title {
  @apply text-sub font-medium text-gray-80;
}

.info-grid {
  @apply flex flex-col gap-[8px];
}

.info-item {
  @apply flex items-center gap-[8px];
}

.info-label {
  @apply text-sub text-gray-60 w-[100px] flex-shrink-0;
}
</style>
